import { ImagePaths } from '../../lib/imageUrls.js';

export const copilotWave3AgenticAi = {
  id: 28,
  slug: 'copilot-wave-3-agentic-ai',
  ogImage: '/og/courses/copilot-wave-3-agentic-ai.jpg',
  courseCode: 'COPILOT-W3-AGENT-001',
  title: 'Microsoft 365 Copilot Wave 3 & Agentic AI for Australian IT Leaders',
  subtitle: 'Govern autonomous AI before you enable it',
  valueProposition: 'Understand Wave 3 agentic capabilities, make informed enablement decisions, and build the governance controls Australian IT leaders need before deploying autonomous Copilot agents.',
  duration: '8 hours',
  students: 'New',
  level: 'Intermediate',
  organization: 'EDUC4TE',
  image: ImagePaths.COURSE_1,
  shortDescription: 'COPILOT-W3-AGENT-001 — Copilot Wave 3 agentic AI governance covering Agent 365, Work IQ, and Copilot Cowork for IT directors and privacy officers.',
  featured: false,
  price: {
    aud: 599,
    currency: 'AUD',
    earlyBird: 449
  },
  stripeConfig: {
    priceId: 'price_1TTdVxKu1nyc322U1fcY7HYc',
    productId: 'prod_USYdudnEjIHntH'
  },
  snapshot: {
    whoItsFor: [
      'IT directors assessing Wave 3 agentic AI for their organisation',
      'Microsoft 365 administrators enabling agent features',
      'Security and governance leads evaluating Copilot Cowork and Work IQ',
      'CISOs and privacy officers in Australian regulated organisations'
    ],
    accreditation: 'Aligned to Privacy Act 1988 Australian Privacy Principles, ASD Blueprint, and ACSC Essential Eight'
  },
  logistics: {
    classSize: 'Maximum 20 participants for scenario-based discussion',
    requirements: [
      'Completion of Copilot Foundations Wave 1 & 2 course or equivalent experience',
      'Familiarity with Microsoft 365 administration and tenant management',
      'Access to a Microsoft 365 tenant for exercises (recommended)',
      'No prior Wave 3 or agentic AI experience required'
    ]
  },
  overview: 'Microsoft 365 Copilot Wave 3 (March 2026) transforms Copilot from a session-bound assistant into a suite of autonomous agents that can plan, execute, and complete multi-step tasks without a human prompt for every action. Agent 365, Researcher, Analyst, and the broader agentic experiences represent a fundamentally different governance challenge from Wave 1 and Wave 2. Work IQ — the persistent intelligence layer that powers all Wave 3 agents — builds a continuous behavioural model of every employee, triggering distinct Privacy Act 1988 obligations. Copilot Cowork, the flagship autonomous task agent, uses Anthropic Claude as a subprocessor, creating a data residency gap outside Australian in-country processing guarantees.\n\nWave 3 began reaching Australian tenants in March 2026, so for most organisations this is no longer a question of if but of what to enable and under what controls. This course is designed for Australian IT leaders who need to make informed enablement decisions on capabilities that are already landing. You will learn how Agent 365, Researcher, and Analyst agents work, what governance controls are available in the Copilot Control System, how to assess Work IQ privacy obligations under APPs 1, 3, 5, and 11, and what due diligence is required before enabling Copilot Cowork in an Australian organisation.\n\nThe course is practical and decision-oriented, built around the governance checkpoints and go/no-go decisions that IT directors, security leads, and privacy officers need to make.',
  outcomes: [
    'Explain the architectural difference between Wave 1 session-bound Copilot and Wave 3 autonomous agents',
    'Understand Agent 365, Researcher, and Analyst agent capabilities and their governance boundaries',
    'Configure Frontier Suite and agent policy controls in the Microsoft 365 admin centre',
    'Assess Work IQ privacy obligations under Privacy Act 1988 APPs 1, 3, 5, and 11',
    'Evaluate Copilot Cowork data residency implications and APP 8 cross-border disclosure requirements',
    'Make informed go/no-go enablement decisions for Wave 3 features in an Australian context',
    'Draft a Work IQ employee disclosure notice meeting APP 5 requirements',
    'Communicate Wave 3 governance risks and controls to executive stakeholders'
  ],
  audience: [
    'IT directors and managers making Wave 3 enablement decisions',
    'Microsoft 365 administrators responsible for agent configuration',
    'Security engineers evaluating agentic AI risk',
    'Privacy and compliance officers assessing Work IQ and Cowork obligations',
    'CISOs in Australian government agencies and regulated industries'
  ],
  prerequisites: [
    'Completion of Copilot Foundations Wave 1 & 2, or equivalent deployment experience',
    'Understanding of Microsoft 365 tenant administration and governance concepts',
    'Familiarity with Australian Privacy Act 1988 at a conceptual level is helpful but not required',
    'No prior Wave 3 or agentic AI experience required'
  ],
  modules: [
    {
      title: 'Module 1: Wave 3 Architecture and the Agentic Shift',
      duration: '1.5 hours',
      topics: [
        'What makes Wave 3 different: autonomous task execution vs session-bound assistance',
        'Agent 365: persistent background task agent capabilities and boundaries',
        'Researcher and Analyst agents: deep research and data analysis at scale',
        'Frontier Suite: the premium agentic tier and Work IQ pricing model',
        'Agentic features across Word, Excel, PowerPoint, and Outlook'
      ]
    },
    {
      title: 'Module 2: Work IQ — The Persistent Intelligence Layer',
      duration: '2 hours',
      topics: [
        'What Work IQ collects: role, work patterns, communication habits, relationships',
        'Which agents consume Work IQ and why disabling agents does not suppress profiling',
        'Privacy Act 1988 obligations: APP 1, APP 3, APP 5, and APP 11 analysis',
        'OAIC guidance on AI systems and employee behavioural data',
        'Drafting a Work IQ employee disclosure notice and privacy policy update',
        'PSPF Privacy Impact Assessment requirements for Australian Government agencies'
      ]
    },
    {
      title: 'Module 3: Copilot Cowork and Data Residency',
      duration: '2 hours',
      topics: [
        'What Copilot Cowork does: autonomous multi-step task execution',
        'The Anthropic Claude subprocessor relationship and why it matters',
        'Australian in-country processing commitments: what is covered and what is excluded',
        'APP 8 cross-border disclosure obligations for Cowork workflows',
        'IRAP assessment boundary: why Cowork sits outside the 2026 assessment scope',
        'Sector-specific implications: government, healthcare, financial services, legal'
      ]
    },
    {
      title: 'Module 4: Agent Governance and Copilot Control System',
      duration: '1.5 hours',
      topics: [
        'Copilot Control System Wave 3 policy levers: agent scoping and permission controls',
        'Configuring Frontier Suite access and agent eligibility policies',
        'Audit logging for agentic task execution via Microsoft Purview',
        'Restricting Cowork to safe workflows pending IRAP boundary resolution',
        'Go/no-go checklist for enabling Wave 3 features in Australian tenants'
      ]
    },
    {
      title: 'Module 5: Deployment Decision Framework',
      duration: '1 hour',
      topics: [
        'Wave 3 governance decision matrix for Australian organisations',
        'Privacy Impact Assessment template for Work IQ and Cowork',
        'Evidence pack requirements: OAIC, APRA, PSPF, and ISM',
        'Communicating Wave 3 risk and readiness to boards and executive sponsors',
        'Next steps: governance and compliance deep dive in Copilot Governance & Compliance course'
      ]
    }
  ],
  format: {
    type: 'Live webinar, self-paced online modules, or lunch-and-learn (8 x 1-hour sessions over 8 days)',
    includes: [
      '8 hours of practical instruction across 5 modules',
      'Wave 3 governance decision matrix and go/no-go checklist',
      'Work IQ employee disclosure notice template',
      'Copilot Cowork APP 8 assessment workbook',
      'Access to recorded sessions for 6 months',
      'Certificate of completion',
      'Email support for 30 days post-course',
      'Links to Copilot Wave 3 & Agentic Shift free ebook download'
    ]
  },
  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Live cohort delivery available on request for teams of 5 or more'
  },
  faqs: [
    {
      question: 'Do I need to complete the Wave 1 & 2 Foundations course first?',
      answer: 'It is strongly recommended. Wave 3 builds directly on the identity, data governance, and tenant controls covered in Foundations. If you have equivalent hands-on Wave 1 and Wave 2 deployment experience, you can enrol directly. Contact us if you are unsure about prerequisites.'
    },
    {
      question: 'What is Work IQ and why does it create privacy obligations?',
      answer: 'Work IQ is the persistent intelligence layer Microsoft announced at Ignite 2025 that builds a continuous behavioural model of every employee — including their role, work patterns, communication habits, and relationships. Because it profiles individuals over time, it constitutes personal information under the Privacy Act 1988, triggering obligations under APPs 1, 3, 5, and 11. Module 2 covers this in full.'
    },
    {
      question: 'Is Copilot Cowork safe to enable in Australian Government tenants?',
      answer: 'Not without additional assessment. The 2026 IRAP assessment covers standard M365 Copilot at Protected level, but Cowork uses Anthropic Claude as a subprocessor and that boundary is explicitly outside the assessed scope. Australian Government agencies must not enable Cowork for Official or Protected workflows until the Anthropic boundary is separately assessed. Module 3 covers this in detail.'
    },
    {
      question: 'How does this course apply to healthcare and financial services organisations?',
      answer: 'Module 3 covers sector-specific implications including APRA CPS 234 obligations for financial services, My Health Records Act obligations for healthcare, and legal professional privilege concerns for legal teams. Each sector has distinct due diligence requirements before enabling Cowork.'
    },
    {
      question: 'Can we enrol multiple team members?',
      answer: 'Yes. For teams of 5 or more, contact us for a private quote. For governance decisions of this significance, we recommend joint enrolment of IT, security, privacy, and legal team members so that the decision-making group shares a common understanding of the risk and controls involved.'
    },
    {
      question: 'What is the Frontier Suite and do we need it?',
      answer: 'The Frontier Suite is the premium Wave 3 tier at AUD 99 per user per month that provides access to Copilot Cowork, Agent 365, and the broader agentic experience surface. Standard Microsoft 365 Copilot licences include some Wave 3 features but not the full Frontier Suite. This course covers what is included in each tier and the governance implications of each.'
    },
    {
      question: 'Will I receive a certificate?',
      answer: 'Yes. Upon completion you will receive a Certificate of Completion from Educ4te confirming your competency in Wave 3 agentic AI governance for Australian organisations. The certificate is suitable for CPD records and LinkedIn.'
    },
    {
      question: 'What is the next course after Wave 3?',
      answer: 'The Copilot Governance & Compliance for Australian Organisations course goes deeper into Microsoft Purview, SharePoint and Teams governance, the Copilot Control System, Cowork APP 8 obligations, and Work IQ privacy controls. The full pathway bundles all four courses at a saving.'
    },
    {
      question: 'Can this course be delivered as lunch-and-learn sessions?',
      answer: 'Yes. The course runs as eight one-hour sessions over eight days, with the same content and outcomes as the standard format, paced around the working day so people do not need to be away from their desks for a full day. Delivery is live online or on-site, and private cohorts are available for teams. Hands-on exercises are spread across the sessions, so participants need access to a Microsoft 365 tenant throughout. Contact us to arrange a schedule.'
    }
  ]
};
